/**
 * OKS-734 — Inertia prop shapes for the brand-side Channel analytics
 * dashboard (`/brand/analytics/channel`).
 *
 * Mirrors the server-side `BrandOps::ChannelAnalytics::Result` Struct
 * shape 1:1 — keys are snake_case (Inertia JSON round-trip preserves the
 * Ruby serializer's keys). Read-only over the OKS-732/733 snapshot tables.
 *
 * # State discriminator (TRD KD-8)
 *
 *   not_connected → no tiktok_connection row; all data props null.
 *   syncing       → connected, zero account snapshots; all data props null.
 *   populated     → chart + KPIs + roster present.
 *   error         → controller rescue; all data props null.
 *
 * Loading is a CLIENT-side skeleton swap during the partial reload — NOT a
 * server state value.
 *
 * # Partial-reload (controller `only:` lock — TRD §3)
 *
 *   router.get(brandAnalyticsChannelPath({ window, metric, sort, dir, page }),
 *     undefined, {
 *       only: CHANNEL_RELOAD_ONLY,
 *       preserveScroll: true, preserveState: false,
 *     })
 */

// =========================================================================
// Filter presets — mirror `BrandOps::ChannelAnalyticsFilters`
// =========================================================================

export const CHANNEL_WINDOW_VALUES = ["7d", "30d", "90d"] as const
export type ChannelWindow = (typeof CHANNEL_WINDOW_VALUES)[number]

export const CHANNEL_METRIC_VALUES = [
  "followers",
  "following",
  "likes",
  "videos",
] as const
export type ChannelMetric = (typeof CHANNEL_METRIC_VALUES)[number]

export const CHANNEL_SORT_VALUES = [
  "views",
  "likes",
  "comments",
  "shares",
] as const
export type ChannelSort = (typeof CHANNEL_SORT_VALUES)[number]

export type ChannelSortDir = "asc" | "desc"

// =========================================================================
// State discriminator
// =========================================================================

export const CHANNEL_ANALYTICS_STATES = [
  "not_connected",
  "syncing",
  "populated",
  "error",
] as const
export type ChannelAnalyticsState = (typeof CHANNEL_ANALYTICS_STATES)[number]

// Locked partial-reload key set — mirrors the controller prop bag exactly
// (TRD §3 / R-3). Centralised here so the filter bar + error retry can't
// drift from the controller.
export const CHANNEL_RELOAD_ONLY = [
  "state",
  "window",
  "metric",
  "connection",
  "reconnect_url",
  "channel",
  "kpis",
  "videos",
] as const

// =========================================================================
// Connection (drives not-connected card + reconnect banner)
// =========================================================================

export interface ChannelConnection {
  /** `connected` | `expired` | `error` (per OKS-462 state machine). */
  status: string
  handle: string | null
  display_name: string | null
  avatar_url: string | null
}

// =========================================================================
// Channel growth chart
// =========================================================================

export interface ChannelSeriesPoint {
  /** UTC day, ISO-8601 (`YYYY-MM-DD`). */
  date: string
  /**
   * Metric value on a present snapshot day (INCLUDING a genuine `0`), or
   * `null` for an ABSENT day (a gap). The chart plots with
   * `connectNulls={false}` so a gap breaks the line while a real 0 renders
   * a plotted point (TRD KD-4). NEVER zero-filled.
   */
  value: number | null
}

export interface ChannelSeries {
  metric: ChannelMetric
  series: ChannelSeriesPoint[]
  /** Current followers (latest in-window, falling back to lifetime). */
  followers_now: number
  /** Net follower change across the window (last − first in-window). */
  followers_delta: number
}

// =========================================================================
// KPI strip
// =========================================================================

export interface ChannelKpis {
  followers_now: number
  followers_delta: number
  total_views: number
  total_likes: number
  total_comments: number
  total_shares: number
}

// =========================================================================
// Video performance table
// =========================================================================

export interface ChannelVideoRow {
  tiktok_video_id: string
  caption: string | null
  thumbnail_url: string | null
  share_url: string | null
  views: number
  likes: number
  comments: number
  shares: number
  /** Windowed Δ per metric (last − first in-window; 0 when < 2 in window). */
  d_views: number
  d_likes: number
  d_comments: number
  d_shares: number
  /** ISO-8601 last-present day. */
  last_seen_on: string
  /** True iff last_seen_on < today − 3 days → "No longer live" (KD-6). */
  stale: boolean
}

export interface ChannelPagination {
  page: number
  pages: number
  count: number
  prev: number | null
  next: number | null
}

export interface ChannelVideos {
  rows: ChannelVideoRow[]
  pagination: ChannelPagination
  sort: ChannelSort
  dir: ChannelSortDir
}

// =========================================================================
// Top-level page props
// =========================================================================

export interface ChannelAnalyticsProps {
  state: ChannelAnalyticsState
  /** URL query echo — drives the window segmented control's active state. */
  window: ChannelWindow
  /** URL query echo — drives the metric select's active state. */
  metric: ChannelMetric
  /** `null` only when there is NO connection row (not_connected). */
  connection: ChannelConnection | null
  /** Brand TikTok connect/reconnect POST URL (js-routes). */
  reconnect_url: string
  /** `null` unless `state === "populated"`. */
  channel: ChannelSeries | null
  /** `null` unless `state === "populated"`. */
  kpis: ChannelKpis | null
  /** `null` unless `state === "populated"`. */
  videos: ChannelVideos | null
}
